import * as React from "react"
import { Link } from "react-router-dom"
import { app, translate } from "../app"
import { languages } from "../locale"
import { isExternalLink, menuContainsExternalLinks, formatMenuTitle } from "../helpers"
import { MenuResources } from "./header"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import { DownOutlined } from "./icons"

interface ResourcesDropdownProps {
  menuResources: MenuResources
}

function ResourceLink({
  link,
  locale,
  children,
}: {
  link: string
  locale: string
  children: React.ReactNode
}) {
  if (isExternalLink(link)) {
    return (
      <a href={link} target="_blank" rel="noopener noreferrer" role="menuitem" className="relative flex cursor-pointer select-none items-center rounded-md px-3 py-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground hover:bg-accent data-[highlighted]:bg-accent data-[disabled]:pointer-events-none data-[disabled]:opacity-50 text-[#111827] font-normal">
        {children}
      </a>
    )
  }

  return <Link role="menuitem" to={`/${locale}${link}`} className="relative flex cursor-pointer select-none items-center rounded-md px-3 py-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground hover:bg-accent data-[highlighted]:bg-accent data-[disabled]:pointer-events-none data-[disabled]:opacity-50 text-[#111827] font-normal">{children}</Link>
}

export function ResourcesDropdown({ menuResources }: ResourcesDropdownProps) {
  const locale = languages[app.locale]?.zendesk?.toLowerCase() ?? app.locale.toLowerCase()
  const isRTL = languages[app.locale]?.rtl
  const uppercaseTitles = menuContainsExternalLinks(menuResources.children)

  if (!menuResources.children || menuResources.children.length === 0) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="nm-trigger cursor-pointer">
          {formatMenuTitle(translate(menuResources.title || {}), uppercaseTitles)}
          <DownOutlined aria-hidden="true" className={`${isRTL ? 'mr-2' : 'ml-2'} dropdown-icon`} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="start">
        <DropdownMenuGroup>
          {menuResources.children.map((category, index) => {
            const categoryTitle = formatMenuTitle(
              translate(category.title || {}),
              uppercaseTitles,
            )
            const categoryLink = category.link || ""
            
            if (category.children && category.children.length > 0) {
              return (
                <DropdownMenuSub key={index}>
                  <DropdownMenuSubTrigger>
                    {categoryTitle}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent align={isRTL ? "start" : "end"}>
                    {category.children.map((subcategory, subIndex) => {
                      const subcategoryTitle = formatMenuTitle(
                        translate(subcategory.title || {}),
                        uppercaseTitles,
                      )
                      const subcategoryLink = subcategory.link || ""
                      return (
                        <DropdownMenuItem key={subIndex} asChild>
                          <ResourceLink link={subcategoryLink} locale={locale}>
                            {subcategoryTitle}
                          </ResourceLink>
                        </DropdownMenuItem>
                      )
                    })}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )
            } else {
              return (
                <DropdownMenuItem key={index} asChild>
                  <ResourceLink link={categoryLink} locale={locale}>
                    {categoryTitle}
                  </ResourceLink>
                </DropdownMenuItem>
              )
            }
          })}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

